/**
 * Nachrichten, Rundschreiben und Neuigkeiten je Basar (Plan Nr. 40, COM-05 bis COM-07).
 * Zielgruppen, Grenzen und Textregeln, gemeinsam für API und Web. Wer welcher Zielgruppe angehört,
 * entscheidet die API je Basar (apps/api/src/messaging/audience.ts), hier steht nur die Liste.
 * Nachrichten sind reiner Klartext: kein HTML, kein Markdown, keine automatischen Links.
 */

export const AUDIENCE_GROUPS = [
  'sellers',
  'applicants',
  'helpers',
  'intake',
  'cashiers',
  'managers',
  'shift_signups',
] as const;
export type AudienceGroup = (typeof AUDIENCE_GROUPS)[number];

export const AUDIENCE_LABELS: Readonly<Record<AudienceGroup, string>> = {
  sellers: 'Freigegebene Verkäufer',
  applicants: 'Bewerber und Warteliste',
  helpers: 'Helfer',
  intake: 'Annahme-Team',
  cashiers: 'Kassen-Team',
  managers: 'Verwaltung',
  shift_signups: 'In Schichten eingetragen',
};

/**
 * Zielgruppen auf Organisationsebene (Plan Nr. 11): Rundschreiben der Organisation an Inhaber und Verwalter bzw.
 * Mitglieder. Eigene Liste, damit Basar-Gruppen und Organisations-Gruppen nie verwechselt werden.
 */
export const ORG_AUDIENCE_GROUPS = ['org_admins', 'org_members'] as const;
export type OrgAudienceGroup = (typeof ORG_AUDIENCE_GROUPS)[number];

export const ORG_AUDIENCE_LABELS: Readonly<Record<OrgAudienceGroup, string>> = {
  org_admins: 'Inhaber und Verwalter',
  org_members: 'Mitglieder',
};

export const MESSAGE_LIMITS = {
  subjectMax: 120,
  textMax: 5000,
  newsTitleMax: 160,
  newsTextMax: 20000,
  broadcastMaxRecipients: 5000,
  broadcastUserIdsMax: 200,
  broadcastsPerEventPerDay: 20,
  /** Höchstens so viele Schichten als Ziel eines Rundschreibens (Plan Nr. 11). */
  broadcastShiftsMax: 50,
  /** Rundschreiben mit Inhalt per E-Mail an Externe je Basar und 24 Stunden. */
  externalBroadcastsPerEventPerDay: 5,
  /** Rundschreiben mit Inhalt per E-Mail an Externe je Organisation (über alle Basare) und 24 Stunden. */
  externalBroadcastsPerOrgPerDay: 10,
  /** Höchstens so viele externe Adressen je Rundschreiben. */
  externalRecipientsMax: 500,
  /** Rundschreiben der Organisation je Organisation und 24 Stunden. */
  orgBroadcastsPerOrgPerDay: 10,
  orgBroadcastMaxRecipients: 2000,
  /** Aufbewahrung der Rundschreiben der Organisation in Tagen nach dem Senden (Entscheidung des Auftraggebers). */
  orgBroadcastRetentionDays: 90,
  /** Neuigkeiten mit E-Mail-Hinweis je Basar und 24 Stunden (ohne Hinweis unbegrenzt) */
  newsNoticesPerEventPerDay: 25,
  threadsPerPersonPerEventPerDay: 10,
  notifyThrottleMinutes: 15,
} as const;

export function isAudienceGroup(v: string): v is AudienceGroup {
  return (AUDIENCE_GROUPS as readonly string[]).includes(v);
}

/** Kommagetrennt für die DB-Spalte, sortiert und ohne Doppelte; wirft bei Unbekanntem. */
export function formatAudiences(groups: readonly AudienceGroup[]): string {
  for (const g of groups) {
    if (!isAudienceGroup(g)) throw new Error('Unbekannte Zielgruppe');
  }
  return [...new Set(groups)].sort().join(',');
}

export function isOrgAudienceGroup(v: string): v is OrgAudienceGroup {
  return (ORG_AUDIENCE_GROUPS as readonly string[]).includes(v);
}

/** Wie `formatAudiences` für die Organisationsebene. */
export function formatOrgAudiences(groups: readonly OrgAudienceGroup[]): string {
  for (const g of groups) {
    if (!isOrgAudienceGroup(g)) throw new Error('Unbekannte Zielgruppe');
  }
  return [...new Set(groups)].sort().join(',');
}

/** Wie `parseAudiences` für die Organisationsebene. */
export function parseOrgAudiences(csv: string | null): OrgAudienceGroup[] {
  if (csv === null || csv === '') return [];
  const result: OrgAudienceGroup[] = [];
  for (const part of csv.split(',')) {
    const value = part.trim();
    if (isOrgAudienceGroup(value) && !result.includes(value)) result.push(value);
  }
  return result.sort();
}

/** Höchstlänge der festgeschriebenen Kennzeichnung (Spalte `audience_label`). */
export const AUDIENCE_LABEL_MAX = 255;

function clip(text: string): string {
  const chars = Array.from(text);
  return chars.length <= AUDIENCE_LABEL_MAX
    ? text
    : `${chars.slice(0, AUDIENCE_LABEL_MAX - 1).join('')}…`;
}

/**
 * Kennzeichnung eines Rundschreibens, die beim Senden festgeschrieben wird, z. B.
 * `an Kassen-Team, Schicht „Kasse Vormittag“`. Gruppen in fester Reihenfolge, Schichten in der gegebenen. Bleibt auch
 * nach Umbenennen oder Löschen einer Schicht richtig, weil der Text gespeichert wird.
 */
export function formatAudienceLabel(input: {
  groups: readonly AudienceGroup[];
  shiftNames?: readonly string[];
  /** Ausdrücklich ausgewählte Personen (nur die Tatsache, nie Namen). */
  hasPeople?: boolean;
}): string {
  const parts: string[] = [];
  for (const group of AUDIENCE_GROUPS) {
    if (input.groups.includes(group)) parts.push(AUDIENCE_LABELS[group]);
  }
  const shifts = (input.shiftNames ?? []).map((name) => `„${name.trim()}“`);
  if (shifts.length === 1) parts.push(`Schicht ${shifts.join('')}`);
  else if (shifts.length > 1) parts.push(`Schichten ${shifts.join(', ')}`);
  if (input.hasPeople === true) parts.push('ausgewählte Personen');
  return clip(parts.length === 0 ? 'an das Team' : `an ${parts.join(', ')}`);
}

/** Kennzeichnung eines Rundschreibens der Organisation, z. B. `an Inhaber und Verwalter`. */
export function formatOrgAudienceLabel(groups: readonly OrgAudienceGroup[]): string {
  const parts = ORG_AUDIENCE_GROUPS.filter((g) => groups.includes(g)).map(
    (g) => ORG_AUDIENCE_LABELS[g],
  );
  return parts.length === 0 ? 'an die Organisation' : `an ${parts.join(', ')}`;
}

/** Unbekannte Einträge werden verworfen (alte Images, spätere Gruppen). */
export function parseAudiences(csv: string | null): AudienceGroup[] {
  if (csv === null || csv === '') return [];
  const result: AudienceGroup[] = [];
  for (const part of csv.split(',')) {
    const value = part.trim();
    if (isAudienceGroup(value) && !result.includes(value)) result.push(value);
  }
  return result.sort();
}

/** Darf eine Person mit diesen Gruppen dem Team des Basars schreiben? (mindestens eine Gruppe) */
export function mayContactTeam(groups: ReadonlySet<AudienceGroup>): boolean {
  return groups.size > 0;
}

/** Nachrichtentext: CRLF und einzelnes CR werden zu LF (Steuerzeichen prüft `hasForbiddenControlChars`). */
export function normalizeMessageText(text: string): string {
  return text.replace(/\r\n?/g, '\n');
}

/**
 * Verboten sind Steuerzeichen außer Zeilenumbruch und Tabulator, DEL, C1-Steuerzeichen,
 * Zeilen-/Absatztrenner und Richtungs-Steuerzeichen (verdeckte Umkehr der Leserichtung).
 */
export function hasForbiddenControlChars(text: string): boolean {
  for (const ch of text) {
    const c = ch.codePointAt(0) ?? 0;
    if (c < 0x20) {
      if (c !== 0x0a && c !== 0x09) return true;
    } else if (c >= 0x7f && c <= 0x9f) {
      return true;
    } else if (c === 0x2028 || c === 0x2029) {
      return true;
    } else if ((c >= 0x202a && c <= 0x202e) || (c >= 0x2066 && c <= 0x2069)) {
      return true;
    }
  }
  return false;
}
